import { message } from "antd";
import UserManagementFunction from "../../../Functions/UserManagement/UserManagement";
import {
  showTransferLicenseConfirm,
  showUCaaSAndSMSDisableConfirm,
} from "../modals/modals";
import { AuthTokens, DataType, TransferError, UserLicenseContext } from "./types";
import { StoredCustomerLicenses } from "../../../Utils/types";
import { CustomerLicense } from "../../../Utils/types";
import { LicenseType } from "../constants/userLicenses";

export const activateUser = async (
  userId: string,
  type: string,
  context: UserLicenseContext,
  autoMailEnabled: boolean
) => {
  try {
    const {
      currentCustomerLicenseId,
      setUserLicenses,
      updateTotalValue,
      AuthTokens,
    } = context;
    const response = await UserManagementFunction.activateUserLicense(
      AuthTokens.accessToken!!,
      AuthTokens.sessionToken!!,
      {
        userId: Number(userId),
        customerLicenseId: currentCustomerLicenseId,
      }
    );
    if (response.status) {
      setUserLicenses((prev) =>
        prev.map((u) =>
          u.id === userId
            ? {
                ...u,
                customerLicenseId: currentCustomerLicenseId,
                licenseId: response.data.id,
              }
            : u
        )
      );
      updateTotalValue(Number(1));
      message.success("User license activated successfully", 3);
      if (type.includes("UCaaS") && autoMailEnabled) {
        UserManagementFunction.sendCredentialsEmail(
          AuthTokens.accessToken!!,
          AuthTokens.sessionToken!!,
          {
            userId: userId,
          }
        ).then((response) => {
          if (!response.status) {
            message.error(response.message, 3);
          } else {
            message.success("Credentials email sent successfully", 3);
          }
        });
      }
      return response.data;
    } else {
      message.error(
        response.message.charAt(0).toUpperCase() + response.message.slice(1),
        3
      );
      return { status: false, message: response.message };
    }
  } catch (error) {
    console.error(error);
  }
};

export const activateUserFlow = async (
  user: any,
  userId: string,
  totalValue: number,
  value: string | undefined,
  type: string,
  showExceededLicenseModal: () => void,
  context: UserLicenseContext,
  autoMailEnabled: boolean
) => {
  const { currentCustomerLicenseId, AuthTokens } = context;
  if (totalValue >= parseInt(value as string)!) {
    showExceededLicenseModal();
    return;
  }

  if (type.includes("UCaaS")) {
    try {
      const response = await UserManagementFunction.getLicensesByUserId(
        AuthTokens.accessToken!!,
        AuthTokens.sessionToken!!,
        { userId: userId }
      );
      if (response.status) {
        const otherLicenses = response.data.filter(
          (license: any) => license.description !== type
        );
        if (otherLicenses.length > 0) {
          showTransferLicenseConfirm(
            user.loginName,
            otherLicenses,
            userId,
            otherLicenses[0].customerLicenseIdOld,
            currentCustomerLicenseId,
            context,
            autoMailEnabled
          );
        } else {
          await activateUser(userId, type, context, autoMailEnabled);
        }
      } else {
        message.error("Error fetching user licenses", 3);
      }
    } catch (error) {
      message.error("Error fetching user licenses", 3);
      console.error(error);
    }
  } else {
    await activateUser(userId, type, context, autoMailEnabled);
  }
};

export const deactivateUser = async (
  licenseId: number,
  userId: string,
  setUserLicenses: React.Dispatch<React.SetStateAction<DataType[]>>,
  updateTotalValue: (value: number) => void,
  AuthTokens: AuthTokens
) => {
  try {
    const { accessToken, sessionToken } = AuthTokens;
    const response = await UserManagementFunction.deactivateUserLicense(
      accessToken!!,
      sessionToken!!,
      {
        id: licenseId.toString(),
      }
    );
    if (response.status) {
      setUserLicenses((prev) =>
        prev.map((u) =>
          u.id === userId
            ? { ...u, customerLicenseId: null, licenseId: null }
            : u
        )
      );
      updateTotalValue(Number(-1));
      message.success("User license deactivated successfully", 3);
    } else {
      message.error(
        response.message.charAt(0).toUpperCase() + response.message.slice(1),
        3
      );
    }
  } catch (error) {
    console.error(error);
  }
};

export const transferUserLicense = async (
  userId: number,
  customerLicenseIdNew: number,
  customerLicenseIdOld: number,
  context: UserLicenseContext,
  autoMailEnabled: boolean
) => {
  try {
    const {
      currentCustomerLicenseId,
      setUserLicenses,
      updateTotalValue,
      onLicenseTransfer,
      AuthTokens,
    } = context;
    const response = await UserManagementFunction.moveUserLicense(
      AuthTokens.accessToken!!,
      AuthTokens.sessionToken!!,
      {
        userId: userId,
        customerLicenseIdOld: customerLicenseIdOld,
        customerLicenseIdNew: customerLicenseIdNew,
      }
    );
    if (response.status) {
      setUserLicenses((prev) =>
        prev.map((u) => {
          if (u.id.toString() === userId.toString()) {
            if (customerLicenseIdOld === currentCustomerLicenseId) {
              return {
                ...u,
                customerLicenseId: null,
                licenseId: null,
                userSMSEnabled: false,
              };
            } else if (customerLicenseIdNew === currentCustomerLicenseId) {
              return {
                ...u,
                customerLicenseId: customerLicenseIdNew,
                licenseId: response.data.id,
              };
            }
          }
          return u;
        })
      );
      if (onLicenseTransfer) {
        onLicenseTransfer();
      }
      updateTotalValue(
        Number(customerLicenseIdNew === currentCustomerLicenseId ? 1 : -1)
      );
      message.success("User license transferred successfully", 3);
      if (autoMailEnabled) {
        UserManagementFunction.sendCredentialsEmail(
          AuthTokens.accessToken!!,
          AuthTokens.sessionToken!!,
          {
            userId: userId.toString(),
          }
        ).then((response) => {
          if (!response.status) {
            message.error(response.message, 3);
          } else {
            message.success("Credentials email sent successfully", 3);
          }
        });
      }
    } else {
      message.error(
        response.message.charAt(0).toUpperCase() + response.message.slice(1),
        3
      );
    }
  } catch (error) {
    console.error(error);
  }
};

export const deactivateUserFlow = async (
  user: any,
  userId: string,
  SMSEnabled: boolean,
  type: string,
  context: UserLicenseContext
) => {
  const { setUserLicenses, updateTotalValue, AuthTokens } = context;
  if (type === LicenseType.UCaaSEnterprise && SMSEnabled) {
    showUCaaSAndSMSDisableConfirm(
      (user?.firstName ?? "") + " " + (user?.lastName ?? ""),
      userId,
      user.licenseId!,
      context
    );
  } else {
    await deactivateUser(
      user.licenseId!,
      userId,
      setUserLicenses,
      updateTotalValue,
      AuthTokens
    );
  }
};

export const deactivateUcaaSAndSMS = async (
  userLicenseId: number,
  userId: string,
  context: UserLicenseContext
) => {
  const {
    currentCustomerLicenseId,
    setUserLicenses,
    updateTotalValue,
    setLoading,
    AuthTokens,
  } = context;
  try {
    const responseSMS = await UserManagementFunction.updateSMSLicense(
      AuthTokens.accessToken!!,
      AuthTokens.sessionToken!!,
      {
        customerLicenseId: currentCustomerLicenseId,
        userId: Number(userId),
        SMSEnabled: false,
      }
    );

    const responseUcaas = await UserManagementFunction.deactivateUserLicense(
      AuthTokens.accessToken!!,
      AuthTokens.sessionToken!!,
      {
        id: userLicenseId.toString(),
      }
    );

    if (responseUcaas.status === true && responseSMS.status === true) {
      setUserLicenses((prev) =>
        prev.map((u) =>
          u.id === userId
            ? {
                ...u,
                customerLicenseId: null,
                licenseId: null,
                userSMSEnabled: false,
              }
            : u
        )
      );
      updateTotalValue(Number(-1));
      message.success("User license deactivated successfully", 3);
    } else {
      const errorMessage = [
        responseUcaas.message.charAt(0).toUpperCase() +
          responseUcaas.message.slice(1),
        responseSMS.message.charAt(0).toUpperCase() +
          responseSMS.message.slice(1),
      ].join(" | ");
      console.log(errorMessage);
      message.error("An error occured while deactivating UCaaS and SMS", 3);
    }
  } catch (error) {
    console.error(error);
  } finally {
    if (setLoading) setLoading(false);
  }
};

export const activateUCaasAndSMS = async (
  userId: string,
  context: UserLicenseContext,
  type: string
) => {
  const {
    currentCustomerLicenseId,
    setUserLicenses,
    updateTotalValue,
    setLoading,
    AuthTokens,
  } = context;
  try {
    const response = await UserManagementFunction.getLicensesByUserId(
      AuthTokens.accessToken!!,
      AuthTokens.sessionToken!!,
      { userId: userId }
    );
    const otherLicenses = response.data.filter(
          (license: any) => license.description !== type
        );
    if (response.status && otherLicenses.length > 0) {
      message.error("Another license is already activated for this user. Please transfer the license.", 3);
      return;
    }
    const responseUcaas = await UserManagementFunction.activateUserLicense(
      AuthTokens.accessToken!!,
      AuthTokens.sessionToken!!,
      {
        userId: Number(userId),
        customerLicenseId: currentCustomerLicenseId,
      }
    );
    const responseSMS = await UserManagementFunction.updateSMSLicense(
      AuthTokens.accessToken!!,
      AuthTokens.sessionToken!!,
      {
        customerLicenseId: currentCustomerLicenseId,
        userId: Number(userId),
        SMSEnabled: true,
      }
    );

    let successMessage = [];
    let errorMessage = [];

    if (responseUcaas.status === true) {
      setUserLicenses((prev) =>
        prev.map((u) =>
          u.id === userId
            ? {
                ...u,
                customerLicenseId: currentCustomerLicenseId,
                licenseId: responseUcaas.data.id,
              }
            : u
        )
      );
      successMessage.push("UCaaS license activated successfully");
    } else {
      errorMessage.push(
        responseUcaas.message.charAt(0).toUpperCase() +
          responseUcaas.message.slice(1)
      );
    }

    if (responseSMS.status === true) {
      setUserLicenses((prev) =>
        prev.map((u) =>
          u.id === userId
            ? {
                ...u,
                userSMSEnabled: true,
              }
            : u
        )
      );
      successMessage.push("SMS license activated successfully");
    } else {
      errorMessage.push(
        responseSMS.message.charAt(0).toUpperCase() +
          responseSMS.message.slice(1)
      );
    }

    if (successMessage.length > 0) {
      updateTotalValue(Number(1));
      message.success(successMessage.join(" | "), 3);
    }

    if (errorMessage.length > 0) {
      console.log(errorMessage);
      message.error("An error occured while activating UCaaS and SMS", 3);
    }
  } catch (error) {
    console.error(error);
    message.error("Failed to activate UCaaS and SMS", 3);
  } finally {
    if (setLoading) setLoading(false);
  }
};

export const hasFullLicenseOfSameType = (
  customerLicenses: StoredCustomerLicenses[],
  trialLicense: CustomerLicense | undefined
) => {

  if (!trialLicense) return null;
  return customerLicenses.find(
    (license) =>
      license.key !== trialLicense?.id &&
      license.licenseId === trialLicense?.licenseId &&
      license.type !== "TRIAL"
  );
}

export const handleTransferErrors = (errorMessages: string[]): TransferError[] => {
  return errorMessages.map(msg => ({
    id: `error-${Math.random().toString(36).slice(2, 9)}`,
    message: msg
  }));
};
